/**
 * The subscribe consent (MM-D-138, TMMAPP-0118 §A1; the Operator's copy, 2026-10-08): two required
 * checkboxes directly above the dialog's actions, never pre-ticked. Box 1 accepts the Terms of
 * Service and the Privacy Policy; each name is a link (a new tab, announced) only once its URL is
 * served, plain text until then. Box 2 states the auto-renewal: the interval, the regular price for
 * it (the quote without a promotion, plus a note naming the promotion: TMMAPP-0119 item 14), and the
 * trial's end when there is one. Until the quote answers it reads "Loading…" and can't be ticked
 * (consent is only to a sentence the person can read); a failed or partial quote reads "We couldn't
 * load the price." with Retry (item 15). An error sits under its box, tied by `aria-describedby`,
 * and marks its box `aria-invalid`, which the dialog focuses (the first one).
 */
import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTheme } from '@mui/material/styles';
import { Trans, useTranslation } from 'react-i18next';
import { MoneyText } from '@/components/MoneyText';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import type { ConsentValues, RenewalPartsTerms, RenewalTerms } from './consumerTerms';

interface SubscribeConsentProps {
  values: ConsentValues;
  onChange: (next: ConsentValues) => void;
  /** Box 2's sentence; null until the quote answers. */
  renewal: RenewalTerms | null;
  /**
   * False when there is no renewal to consent to (the first-sign-in dialog with no served
   * `subscription.renewal`, TMMAPP-0124 item 6): box 2 is not shown.
   */
  autoRenewalShown?: boolean | undefined;
  /** The quote failed, or answered without the trial or the interval's total: Retry asks again. */
  priceFailed: boolean;
  onRetryPrice: () => void;
  /** SUBSCRIBE was pressed while box 2 was loading: "We're still loading your price…" under it. */
  priceLoadingShown: boolean;
  /** The served `consumerTermsUrl` / `privacyPolicyUrl` (TMMAPI-0156 U1); null is plain text. */
  termsUrl: string | null;
  privacyUrl: string | null;
  /** The message under each box, or null. */
  termsError: string | null;
  autoRenewalError: string | null;
  disabled: boolean;
  idPrefix: string;
}

function DocumentName({ url, children }: { url: string | null; children?: ReactNode }) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  if (url === null) return <>{children}</>;
  return (
    <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ color: tokens.link }}>
      {children}
      {/* The space is inside the hidden text: nothing visible (or underlined) follows the name. */}
      <Box component="span" sx={visuallyHidden}>
        {` ${t('subscribeDialog.consent.opensInNewTab')}`}
      </Box>
    </Link>
  );
}

export function SubscribeConsent({
  values,
  onChange,
  renewal,
  autoRenewalShown = true,
  priceFailed,
  onRetryPrice,
  priceLoadingShown,
  termsUrl,
  privacyUrl,
  termsError,
  autoRenewalError,
  disabled,
  idPrefix,
}: SubscribeConsentProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const termsErrorId = `${idPrefix}-terms-error`;
  const renewalErrorId = `${idPrefix}-renewal-error`;
  const renewalLoadingId = `${idPrefix}-renewal-loading`;
  const stated = renewal !== null;
  const loadingShown = !stated && !priceFailed && priceLoadingShown;

  return (
    <Stack spacing={1} data-subscribe-consent="">
      <Box>
        <FormControlLabel
          sx={{ alignItems: 'flex-start', marginInlineEnd: 0 }}
          control={
            <Checkbox
              checked={values.terms}
              disabled={disabled}
              onChange={(_event, checked) => onChange({ ...values, terms: checked })}
              sx={{ mt: -1 }}
              slotProps={{
                input: {
                  'aria-invalid': termsError !== null || undefined,
                  'aria-describedby': termsError !== null ? termsErrorId : undefined,
                },
              }}
            />
          }
          label={
            <Trans
              i18nKey="subscribeDialog.consent.terms"
              components={{
                terms: <DocumentName url={termsUrl} />,
                privacy: <DocumentName url={privacyUrl} />,
              }}
            />
          }
        />
        {termsError !== null && (
          <FormHelperText error id={termsErrorId} sx={{ paddingInlineStart: 4 }}>
            {termsError}
          </FormHelperText>
        )}
      </Box>
      {autoRenewalShown && (
        <Box>
          <FormControlLabel
            sx={{ alignItems: 'flex-start', marginInlineEnd: 0 }}
            control={
              <Checkbox
                checked={values.autoRenewal}
                disabled={disabled || !stated}
                onChange={(_event, checked) => onChange({ ...values, autoRenewal: checked })}
                sx={{ mt: -1 }}
                slotProps={{
                  input: {
                    'aria-invalid': autoRenewalError !== null || undefined,
                    'aria-describedby': autoRenewalError !== null ? renewalErrorId : undefined,
                  },
                }}
              />
            }
            label={
              renewal !== null ? (
                <RenewalSentence renewal={renewal} />
              ) : priceFailed ? (
                t('subscribeDialog.consent.priceFailed')
              ) : (
                t('common.loading')
              )
            }
          />
          {renewal?.parts != null && renewal.trialNotice != null && (
            <Typography
              variant="body2"
              component="p"
              data-trial-ends=""
              sx={{ paddingInlineStart: 4, color: 'text.secondary' }}
            >
              {t('settings.trialEnds', { on: formatCivilDate(renewal.trialNotice, locale) })}
            </Typography>
          )}
          {!stated && priceFailed && (
            <Box sx={{ paddingInlineStart: 4 }}>
              <Button size="small" onClick={onRetryPrice} data-renewal-retry="">
                {t('common.retry')}
              </Button>
            </Box>
          )}
          {loadingShown && (
            <Typography
              variant="body2"
              id={renewalLoadingId}
              tabIndex={-1}
              role="status"
              data-renewal-loading=""
              sx={{ paddingInlineStart: 4, color: 'text.secondary' }}
            >
              {t('subscribeDialog.consent.priceLoading')}
            </Typography>
          )}
          {autoRenewalError !== null && (
            <FormHelperText error id={renewalErrorId} sx={{ paddingInlineStart: 4 }}>
              {autoRenewalError}
            </FormHelperText>
          )}
        </Box>
      )}
    </Stack>
  );
}

/** Box 2's sentence keys: [no trial, with a trial] per interval, without and with a promotion. */
const RENEWAL_KEYS = {
  plain: {
    MONTH: ['subscribeDialog.consent.renewalMonth', 'subscribeDialog.consent.renewalTrialMonth'],
    YEAR: ['subscribeDialog.consent.renewalYear', 'subscribeDialog.consent.renewalTrialYear'],
  },
  promo: {
    MONTH: [
      'subscribeDialog.consent.renewalPromoMonth',
      'subscribeDialog.consent.renewalPromoTrialMonth',
    ],
    YEAR: [
      'subscribeDialog.consent.renewalPromoYear',
      'subscribeDialog.consent.renewalPromoTrialYear',
    ],
  },
} as const;

type PartsSentenceKey =
  | 'subscribeDialog.consent.renewalPartsMonth'
  | 'subscribeDialog.consent.renewalPartsYear'
  | 'subscribeDialog.consent.renewalPartsAddOnsMonth'
  | 'subscribeDialog.consent.renewalPartsAddOnsYear'
  | 'subscribeDialog.consent.renewalPartsAddOnsPromoMonth'
  | 'subscribeDialog.consent.renewalPartsAddOnsPromoYear';

/** One catalog sentence per variant. A promotion never joins a second string onto the plan. */
function partsSentenceKey(
  interval: RenewalTerms['interval'],
  parts: RenewalPartsTerms,
): PartsSentenceKey {
  if (parts.includesPromotion) {
    return interval === 'YEAR'
      ? 'subscribeDialog.consent.renewalPartsAddOnsPromoYear'
      : 'subscribeDialog.consent.renewalPartsAddOnsPromoMonth';
  }
  if (parts.addOnsMinor !== 0) {
    return interval === 'YEAR'
      ? 'subscribeDialog.consent.renewalPartsAddOnsYear'
      : 'subscribeDialog.consent.renewalPartsAddOnsMonth';
  }
  return interval === 'YEAR'
    ? 'subscribeDialog.consent.renewalPartsYear'
    : 'subscribeDialog.consent.renewalPartsMonth';
}

function PartsRenewalSentence({ renewal }: { renewal: RenewalTerms }) {
  const locale = useLocale();
  const parts = renewal.parts;
  if (parts == null) return null;
  return (
    <Trans
      i18nKey={partsSentenceKey(renewal.interval, parts)}
      values={{ date: formatCivilDate(parts.on, locale) }}
      components={{
        price: <MoneyText minor={parts.baseMinor} currency={renewal.currency} />,
        addOns: <MoneyText minor={parts.addOnsMinor} currency={renewal.currency} />,
      }}
    />
  );
}

function RenewalSentence({ renewal }: { renewal: RenewalTerms }) {
  const { t } = useTranslation();
  const locale = useLocale();
  if (renewal.parts != null) return <PartsRenewalSentence renewal={renewal} />;
  const { interval, priceMinor, currency, trialEnd, promo } = renewal;
  const key = RENEWAL_KEYS[promo !== null ? 'promo' : 'plain'][interval][trialEnd !== null ? 1 : 0];
  // {n} is the served statement count; without it, "lowers your first statements".
  const promoNote =
    promo === null
      ? ''
      : promo.statements !== null
        ? t('subscribeDialog.consent.promoNote', { code: promo.code, count: promo.statements })
        : t('subscribeDialog.consent.promoNotePlain', { code: promo.code });
  return (
    <Trans
      i18nKey={key}
      values={{ trialEnd: trialEnd !== null ? formatCivilDate(trialEnd, locale) : '', promoNote }}
      components={{ price: <MoneyText minor={priceMinor} currency={currency} /> }}
    />
  );
}
